// ─── Shrinkflation detection runner ─────────────────────────────────────────
// Volume Tracker Commit 3.
//
// SCOPE
// Runs after a successful invoice upload (saveInvoiceItems wraps a call to
// runShrinkflationDetection in a try/catch — a detection failure must never
// break the upload). Evaluates each affected food_item against two signals:
//
//   Signal A — per_lb_spike (catch-weight items only)
//     $/lb up ≥5% across recent 2 invoices vs trailing 90d median, AND
//     $/case has stayed within ±2% of its trailing median.
//
//     SCOPE LIMITED TO is_catch_weight = TRUE. Documented in
//     DESIGN_SYSTEM.md §Vendor Watch — the conceptual pattern (case price
//     flat while $/lb rises) is mathematically possible only when case
//     weight varies, which is by definition catch-weight. Applying Signal A
//     to weight-trackable fixed-pack items would generate noise from natural
//     CV (up to 25% observed in current data) without ever capturing real
//     shrinkflation.
//
//   Signal B — pack_count_drop (fixed-pack items only)
//     Most-recent invoice pack_count below the trailing 90d mode by ≥5%.
//
//   Signal C — case_weight_drop
//     DEFERRED. Pack data quality currently insufficient (corn-dog parser
//     incident: 67 OZ vs 0.67 OZ). Will ship after parser guardrails.
//
// LIFECYCLE
//   1st triggering invoice  → 'watching' row inserted
//   2nd triggering invoice  → promote watching → 'active' (visible in UI)
//   user dismisses          → 'dismissed' + cooled_down_until = now() + 30d
//   value returns to base   → 'resolved' + same cooldown (only if previously active)
//   watching contradicted   → row DELETED (never surfaced, no audit value)
//
//   Cooldown is lazy-checked here on each detection run — no scheduled job.
//
// IDEMPOTENCY
//   The shrinkflation_alerts table has UNIQUE (company_id, food_item_id,
//   signal_type, status) DEFERRABLE INITIALLY DEFERRED. Running the same
//   detection twice on the same data is a no-op for inserts; the promotion
//   path is an UPDATE.

import { supabase } from '../supabase';

// ─── Operator-facing description helpers ────────────────────────────────────
// Single source of truth for the human-readable signal language. VendorWatch
// (top-of-page card) and the inline expandable chip on item rows both import
// these so the wording stays identical across surfaces.

/**
 * describeAlert(alert) → string
 *   per_lb_spike    "$/lb up 10.0% over last 2 invoices · case price flat"
 *   pack_count_drop "Pack count dropped from 45 → 38"
 * Falls back to a debug-style string for unknown signal types.
 */
export function describeAlert(alert) {
  if (!alert) return '';
  if (alert.signal_type === 'per_lb_spike') {
    const dir = alert.change_pct >= 0 ? 'up' : 'down';
    return `$/lb ${dir} ${Math.abs(Number(alert.change_pct)).toFixed(1)}% over last 2 invoices · case price flat`;
  }
  if (alert.signal_type === 'pack_count_drop') {
    return `Pack count dropped from ${Math.round(Number(alert.baseline_value))} \u2192 ${Math.round(Number(alert.current_value))}`;
  }
  return `${alert.signal_type}: ${Number(alert.change_pct).toFixed(1)}%`;
}

/**
 * formatDetectionAge(iso) → "today" / "3 days ago" / "2 weeks ago"
 */
export function formatDetectionAge(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks === 1) return '1 week ago';
  return `${weeks} weeks ago`;
}

const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;
const COOLDOWN_DAYS = 30;
const SIGNAL_A_PCT_THRESHOLD = 0.05;   // 5% — see DESIGN_SYSTEM.md §Vendor Watch threshold rationale
const SIGNAL_A_CASE_FLAT_GATE = 0.02;  // case price must stay within ±2% to count as "flat"
const SIGNAL_A_RESOLVE_BAND = 0.02;    // ±2% from baseline marks "resolved"
const SIGNAL_B_PCT_THRESHOLD = 0.05;   // 5%
const MIN_HISTORY_LINES = 3;

function median(arr) {
  if (!arr || arr.length === 0) return null;
  const sorted = arr.slice().sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}
function mode(arr) {
  if (!arr || arr.length === 0) return null;
  const counts = new Map();
  let best = arr[0], bestN = 0;
  for (const v of arr) {
    const n = (counts.get(v) || 0) + 1;
    counts.set(v, n);
    if (n > bestN) { best = v; bestN = n; }
  }
  return best;
}
function avg(arr) {
  if (!arr || arr.length === 0) return null;
  return arr.reduce((s, v) => s + v, 0) / arr.length;
}

/**
 * Evaluate Signal A for a single item's line history.
 * Returns { fires: bool, baseline, current, changePct, casePct, confirmingIds: number[] }
 * or null if ineligible.
 */
function evalSignalA(item, lines) {
  if (!item?.is_catch_weight) return null;
  const eligible = lines.filter(l => l.price_per_lb != null && l.unit_price != null);
  if (eligible.length < MIN_HISTORY_LINES) return null;

  // Lines are sorted DESC by invoice_date already (caller's responsibility).
  const recent2 = eligible.slice(0, 2);
  const baselineLines = eligible.slice(2);
  if (baselineLines.length < 1) return null;

  const baselinePerLb = median(baselineLines.map(l => Number(l.price_per_lb)));
  const baselineCase  = median(baselineLines.map(l => Number(l.unit_price)));
  const recentPerLb   = avg(recent2.map(l => Number(l.price_per_lb)));
  const recentCase    = avg(recent2.map(l => Number(l.unit_price)));

  if (!baselinePerLb || !baselineCase) return null;

  const perLbChange = (recentPerLb - baselinePerLb) / baselinePerLb;
  const caseChange  = (recentCase  - baselineCase)  / baselineCase;

  const fires = perLbChange >= SIGNAL_A_PCT_THRESHOLD
             && Math.abs(caseChange) < SIGNAL_A_CASE_FLAT_GATE;

  // Both lines in recent2 individually elevated → eligible for promotion to active.
  const bothConfirm = recent2.length === 2
    && recent2.every(l => (Number(l.price_per_lb) - baselinePerLb) / baselinePerLb >= SIGNAL_A_PCT_THRESHOLD);

  return {
    fires,
    baseline: baselinePerLb,
    current: recentPerLb,
    changePct: perLbChange * 100,
    casePct: caseChange * 100,
    bothConfirm,
    confirmingIds: recent2.map(l => l.id),
  };
}

/**
 * Evaluate Signal B for a single item's line history.
 * Returns { fires, baseline, current, changePct, confirmingIds } or null.
 */
function evalSignalB(item, lines) {
  if (item?.is_catch_weight) return null;
  const eligible = lines.filter(l => l.pack_count != null);
  if (eligible.length < MIN_HISTORY_LINES) return null;

  const recent2 = eligible.slice(0, 2);
  const baselineLines = eligible.slice(2);
  if (baselineLines.length < 1) return null;

  const baselinePc = mode(baselineLines.map(l => Number(l.pack_count)));
  const recentPc   = Number(recent2[0].pack_count);

  if (!baselinePc) return null;

  const drop = (baselinePc - recentPc) / baselinePc;
  const fires = recentPc < baselinePc && drop >= SIGNAL_B_PCT_THRESHOLD;

  // Promotion eligibility: both recent lines below baseline.
  const bothConfirm = recent2.length === 2
    && recent2.every(l => Number(l.pack_count) < baselinePc
                       && (baselinePc - Number(l.pack_count)) / baselinePc >= SIGNAL_B_PCT_THRESHOLD);

  return {
    fires,
    baseline: baselinePc,
    current: recentPc,
    changePct: -drop * 100, // negative = drop, signed convention
    bothConfirm,
    confirmingIds: recent2.map(l => l.id),
  };
}

/**
 * Detection runner.
 *
 * @param {object} opts
 * @param {number|string} opts.companyId         — required
 * @param {Array<number>} [opts.foodItemIds]    — limit detection to these items.
 *                                                 If omitted, evaluates all items
 *                                                 for the company.
 * @param {boolean} [opts.dryRun=false]          — when true, computes everything
 *                                                 but writes nothing. Returns the
 *                                                 would-fire alert proposals so
 *                                                 callers can render them.
 * @returns {Promise<{ created: object[], promoted: object[], deleted: object[],
 *                     resolved: object[], wouldFire: object[] }>}
 */
export async function runShrinkflationDetection({ companyId, foodItemIds, dryRun = false }) {
  if (!companyId) throw new Error('runShrinkflationDetection: companyId required');

  // 1. Load items + lines for the affected scope.
  let itemsQuery = supabase.from('food_items')
    .select('id, normalized_name, is_catch_weight, weight_per_case, vendor_name')
    .eq('company_id', companyId);
  if (foodItemIds && foodItemIds.length > 0) {
    itemsQuery = itemsQuery.in('id', foodItemIds);
  }
  const { data: items, error: itemsErr } = await itemsQuery;
  if (itemsErr) throw itemsErr;
  if (!items || items.length === 0) return { created: [], promoted: [], deleted: [], resolved: [], wouldFire: [] };

  const ids = items.map(i => i.id);
  const ninetyDaysAgo = new Date(Date.now() - NINETY_DAYS_MS).toISOString().split('T')[0];

  const { data: lines, error: linesErr } = await supabase.from('invoice_line_items')
    .select('id, food_item_id, invoice_date, unit_price, price_per_lb, pack_count, total_weight, vendor_name')
    .in('food_item_id', ids)
    .eq('company_id', companyId)
    .gte('invoice_date', ninetyDaysAgo)
    .order('invoice_date', { ascending: false })
    .order('id', { ascending: false });
  if (linesErr) throw linesErr;

  const linesByItem = {};
  (lines || []).forEach(l => {
    if (!linesByItem[l.food_item_id]) linesByItem[l.food_item_id] = [];
    linesByItem[l.food_item_id].push(l);
  });

  // 2. Load existing alerts for these items (any status) so we can decide
  //    create vs promote vs skip vs delete.
  const { data: existing, error: existErr } = await supabase.from('shrinkflation_alerts')
    .select('*')
    .in('food_item_id', ids)
    .eq('company_id', companyId);
  if (existErr) throw existErr;

  const existingByKey = {}; // `${food_item_id}|${signal_type}|${status}` → row
  (existing || []).forEach(a => {
    existingByKey[`${a.food_item_id}|${a.signal_type}|${a.status}`] = a;
  });

  const created = [];
  const promoted = [];
  const deleted = [];
  const resolved = [];
  const wouldFire = []; // for dry-run mode

  const nowIso = new Date().toISOString();
  const cooldownIso = new Date(Date.now() + COOLDOWN_DAYS * 24 * 60 * 60 * 1000).toISOString();

  for (const item of items) {
    const itemLines = linesByItem[item.id] || [];

    // Evaluate both signals; only one will fire for a given item type.
    const signalA = evalSignalA(item, itemLines);
    const signalB = evalSignalB(item, itemLines);

    for (const evald of [
      { signal_type: 'per_lb_spike',     result: signalA },
      { signal_type: 'pack_count_drop', result: signalB },
    ]) {
      if (!evald.result) continue;
      const r = evald.result;
      const sig = evald.signal_type;

      // Check cooldown — skip if a cooled_down row blocks re-fire.
      const cooled = existingByKey[`${item.id}|${sig}|cooled_down`];
      if (cooled && cooled.cooled_down_until && new Date(cooled.cooled_down_until) > new Date()) {
        // Allow re-fire only if the new value drifts FURTHER than the cooldown's recorded value.
        const cooledChange = Math.abs(Number(cooled.change_pct) || 0);
        const newChange = Math.abs(r.changePct);
        if (newChange <= cooledChange + 0.01) continue;
      }

      const watching = existingByKey[`${item.id}|${sig}|watching`];
      const active   = existingByKey[`${item.id}|${sig}|active`];

      // CASE 1: signal fires.
      if (r.fires) {
        const proposalRow = {
          company_id: companyId,
          food_item_id: item.id,
          signal_type: sig,
          baseline_value: r.baseline,
          current_value: r.current,
          change_pct: r.changePct,
          confirming_invoices: r.confirmingIds,
          item_name: item.normalized_name,
          would_promote: !!watching && r.bothConfirm,
          status: r.bothConfirm ? 'active' : (watching ? watching.status : 'watching'),
        };

        if (dryRun) { wouldFire.push(proposalRow); continue; }

        if (active) {
          // Already active — refresh the current_value/change_pct/confirmingIds.
          await supabase.from('shrinkflation_alerts')
            .update({
              current_value: r.current,
              change_pct: r.changePct,
              confirming_invoices: r.confirmingIds,
              updated_at: nowIso,
            })
            .eq('id', active.id);
          continue;
        }

        if (watching && r.bothConfirm) {
          // Promote watching → active.
          const { data: updated } = await supabase.from('shrinkflation_alerts')
            .update({
              status: 'active',
              promoted_at: nowIso,
              current_value: r.current,
              change_pct: r.changePct,
              confirming_invoices: r.confirmingIds,
              updated_at: nowIso,
            })
            .eq('id', watching.id)
            .select()
            .single();
          if (updated) promoted.push(updated);
          continue;
        }

        if (!watching) {
          // Create watching row. ON CONFLICT no-op (idempotent across reruns).
          const { data: ins } = await supabase.from('shrinkflation_alerts')
            .insert({
              company_id: companyId,
              food_item_id: item.id,
              signal_type: sig,
              status: 'watching',
              baseline_value: r.baseline,
              current_value: r.current,
              change_pct: r.changePct,
              confirming_invoices: r.confirmingIds,
            })
            .select()
            .single();
          if (ins) created.push(ins);
          continue;
        }
        // watching exists, but bothConfirm is false → still watching, refresh values.
        await supabase.from('shrinkflation_alerts')
          .update({
            current_value: r.current,
            change_pct: r.changePct,
            confirming_invoices: r.confirmingIds,
            updated_at: nowIso,
          })
          .eq('id', watching.id);
        continue;
      }

      // CASE 2: signal does NOT fire.
      // 2a. If a watching row exists and the new reading contradicts the signal,
      //     DELETE the watching row (per spec — never surfaced, no audit value).
      if (watching && !r.fires) {
        if (dryRun) continue;
        await supabase.from('shrinkflation_alerts').delete().eq('id', watching.id);
        deleted.push(watching);
        continue;
      }

      // 2b. If an active row exists and value has returned to baseline (within
      //     ±2% band), mark as resolved + 30-day cooldown.
      if (active && Math.abs(r.changePct / 100) < SIGNAL_A_RESOLVE_BAND) {
        if (dryRun) continue;
        await supabase.from('shrinkflation_alerts')
          .update({
            status: 'resolved',
            resolved_at: nowIso,
            cooled_down_until: cooldownIso,
            current_value: r.current,
            change_pct: r.changePct,
            updated_at: nowIso,
          })
          .eq('id', active.id);
        resolved.push(active);
      }
    }
  }

  return { created, promoted, deleted, resolved, wouldFire };
}
